import { useEffect, useState } from "react"
import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react"
import { Outlet, useParams } from "react-router-dom"
import jwtDecode from "jwt-decode"
import { useTranslation } from "react-i18next"
import { ErrorPage } from "../ErrorPage"
import LaunchDarklyProvider from "../../providers/LaunchDarklyProvider"

interface DecodedToken {
  "http://localhost/roles"?: string[]
}

enum AuthStage {
  UNKNOWN, // User is not logged in yet, unsure of role.
  AUTHORIZED, // User is authenticated and has a required role
  UNAUTHORIZED, // User is authenticated but doesn't have a required role
}

const ALLOWED_ROLES = ["referral-nlo", "referral-admin"]

export function ProtectedRoute() {
  const { t } = useTranslation()
  const { province } = useParams<{ province: string }>()
  const { isAuthenticated, getIdTokenClaims } = useAuth0()
  const [authStage, setAuthStage] = useState<AuthStage>(AuthStage.UNKNOWN)

  useEffect(() => {
    async function checkUserRole() {
      const roles = "http://localhost/roles"
      const tokenClaims = await getIdTokenClaims()
      const decodedToken: DecodedToken = jwtDecode(tokenClaims?.__raw || "")
      const hasRole = decodedToken[roles]?.some((role) =>
        ALLOWED_ROLES.includes(role),
      )
      setAuthStage(hasRole ? AuthStage.AUTHORIZED : AuthStage.UNAUTHORIZED)
    }

    if (isAuthenticated) {
      checkUserRole()
    }
  }, [isAuthenticated, getIdTokenClaims])

  if (province && province.toLowerCase() !== "ontario") {
    return <Outlet />
  }

  if (authStage === AuthStage.UNAUTHORIZED) {
    return (
      <div className="flex flex-1 mt-20 justify-center h-[100vh]">
        <ErrorPage
          title={t("client.auth.unAuthAccess")}
          errorMessage={t("client.auth.noAccess")}
        />
      </div>
    )
  }

  const Component = withAuthenticationRequired(Outlet)
  return (
    <LaunchDarklyProvider>
      <Component />
    </LaunchDarklyProvider>
  )
}
